import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import {
    Configuration,
    RouletteRacetrackBetsConfigurationAttributes,
    RouletteWheelConfigurationAttributes,
} from "../Components/Configuration";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { RouletteRacetrackConfiguration } from "../CustomComponents/RouletteRacetrackConfiguration";
import { HtmlRacetrackBetsElement } from "../HTMLComponents/Custom/HtmlRacetrackBetsElement";
import { RouletteActions } from "../Channels/Common/Actions/RouletteActions";
import { BetManager } from "../Managers/BetManager";
import { HtmlRouletteTableElement } from "../HTMLComponents/Custom/HtmlRouletteTableElement";
import { HtmlGridEdgeBetsElement } from "../HTMLComponents/Custom/HtmlGridEdgeBetsElement";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { RouletteBetType } from "../CustomComponents/RouletteBetType";
import { StatisticsManager } from "../Managers/StatisticsManager";
import { AppContext } from "../../Common/AppContext";

@engineComponent(ComponentTypes.DisplayObject)
export class Racetrack extends ContainerElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static readonly NeighboursPreviewDuration: number = 20; //in frames

    private static Instance: Racetrack;

    protected static TiersActions: Array<string>;

    protected static OrphelinsActions: Array<string>;

    protected static VoisinsActions: Array<string>;

    protected static ZeroGameActions: Array<string>;

    protected static Neighbours: Array<number> = [2];

    protected static NeighboursIndex: number = 0;

    protected static RacetrackStrip: Array<number>;

    protected racetrackElements: { [id: string]: SwitchElement };

    protected htmlRacetrack: HtmlRacetrackBetsElement;

    protected neighboursPreviewTimer: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.racetrackElements = {};

        this.neighboursPreviewTimer = CreateFrameTimer("magnifierTimer");
        this.neighboursPreviewTimer.on(FrameTimerEvents.Complete, this.neighboursPreviewEnd.bind(this));

        Racetrack.Instance = this;
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(RacetrackAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(RacetrackAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    private configurate(node: Element): void {
        if (node.hasAttribute(RouletteWheelConfigurationAttributes.WHEEL_STRIP)) {
            Racetrack.RacetrackStrip = ArrayUtils.numberArray(
                node.getAttribute(RouletteWheelConfigurationAttributes.WHEEL_STRIP),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.WIDTH)) {
            RouletteRacetrackConfiguration.RacetrackWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.HEIGHT)) {
            RouletteRacetrackConfiguration.RacetrackHeight = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.SINGLE_WIDTH)) {
            RouletteRacetrackConfiguration.SingleWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.SINGLE_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.SINGLE_HEIGHT)) {
            RouletteRacetrackConfiguration.SingleHeight = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.SINGLE_HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.TIERS_WIDTH)) {
            RouletteRacetrackConfiguration.TiersWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.TIERS_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.ORPHELINS_WIDTH)) {
            RouletteRacetrackConfiguration.OrphelinsWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.ORPHELINS_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.VOISINS_WIDTH)) {
            RouletteRacetrackConfiguration.VoisinsWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.VOISINS_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.ZERO_GAME_WIDTH)) {
            RouletteRacetrackConfiguration.ZeroGameWidth = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.ZERO_GAME_WIDTH),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.MIDDLE_HEIGHT)) {
            RouletteRacetrackConfiguration.MiddleHeight = parseInt(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.MIDDLE_HEIGHT),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.TIERS)) {
            Racetrack.TiersActions = ArrayUtils.stringArray(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.TIERS),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.ORPHELINS)) {
            Racetrack.OrphelinsActions = ArrayUtils.stringArray(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.ORPHELINS),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.VOISINS)) {
            Racetrack.VoisinsActions = ArrayUtils.stringArray(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.VOISINS),
            );
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.ZERO_GAME)) {
            Racetrack.ZeroGameActions = ArrayUtils.stringArray(
                node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.ZERO_GAME),
            );
        }

        /*if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.NEIGHBOURS)) {
            Racetrack.Neighbours = ArrayUtils.numberArray(node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.NEIGHBOURS));
        }

        if (node.hasAttribute(RouletteRacetrackBetsConfigurationAttributes.NEIGHBOURS_DEFALUT)) {
            let neighboursDefaultValue = parseInt(node.getAttribute(RouletteRacetrackBetsConfigurationAttributes.NEIGHBOURS_DEFALUT));
            let index = Racetrack.Neighbours.indexOf(neighboursDefaultValue);

            if(index > -1) {
                Racetrack.NeighboursIndex = index;
            }
        }*/

        Racetrack.Neighbours = AppContext.gameConfiguration.NeighborNumbers;
        const neighboursDefaultValue = AppContext.gameConfiguration.NeighborNumbersDefault;
        let index = Racetrack.Neighbours.indexOf(neighboursDefaultValue);

        if (Racetrack.Neighbours.length == 0) {
            Racetrack.Neighbours.push(1);
        }

        if (index > -1) {
            Racetrack.NeighboursIndex = index;
        }
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(): void {
        this.createRacetrackBets();
        this.createHtmlLayer();
    }

    private createRacetrackBets(): void {
        if (this.childs["RacetrackNumbers"]) {
            let racetrackNumbers = <ContainerElement>this.childs["RacetrackNumbers"];

            if (racetrackNumbers) {
                for (let i = 0; i < Racetrack.RacetrackStrip.length; i++) {
                    let number = <SwitchElement>racetrackNumbers.childs["R_" + Racetrack.RacetrackStrip[i]];

                    if (number) {
                        let image = <ImageTextElement>number.getChildElementByName("Text");

                        if (image) {
                            image.bitmapText.text = Racetrack.RacetrackStrip[i].toString();
                            image.realign();
                        }

                        this.racetrackElements["R_" + Racetrack.RacetrackStrip[i]] = number;
                        StatisticsManager.addHitMapElement("N_" + Racetrack.RacetrackStrip[i], number); //keep same id as grid numbers
                    }
                }

                if (racetrackNumbers.childs["R_tiers"]) {
                    this.racetrackElements["R_tiers"] = <SwitchElement>racetrackNumbers.childs["R_tiers"];
                    StatisticsManager.addHitMapElement("R_tiers", <SwitchElement>racetrackNumbers.childs["R_tiers"]);
                }

                if (racetrackNumbers.childs["R_orphelins"]) {
                    this.racetrackElements["R_orphelins"] = <SwitchElement>racetrackNumbers.childs["R_orphelins"];
                    StatisticsManager.addHitMapElement(
                        "R_orphelins",
                        <SwitchElement>racetrackNumbers.childs["R_orphelins"],
                    );
                }

                if (racetrackNumbers.childs["R_voisins"]) {
                    this.racetrackElements["R_voisins"] = <SwitchElement>racetrackNumbers.childs["R_voisins"];
                    StatisticsManager.addHitMapElement(
                        "R_voisins",
                        <SwitchElement>racetrackNumbers.childs["R_voisins"],
                    );
                }

                if (racetrackNumbers.childs["R_zeroGame"]) {
                    this.racetrackElements["R_zeroGame"] = <SwitchElement>racetrackNumbers.childs["R_zeroGame"];
                    StatisticsManager.addHitMapElement(
                        "R_zeroGame",
                        <SwitchElement>racetrackNumbers.childs["R_zeroGame"],
                    );
                }
            }
        }
    }

    private createHtmlLayer(): void {
        this.htmlRacetrack = new HtmlRacetrackBetsElement(
            "mainTableControls",
            "racetrackBetPlaces",
            "racetrackBetPlaces",
            this.racetrackElements,
            this,
            RouletteRacetrackConfiguration.RacetrackWidth,
            RouletteRacetrackConfiguration.RacetrackHeight,
        );

        HtmlRouletteTableElement.Emitter.on(HtmlGridEdgeBetsElement.MouseOverBet, this.onMouseOver.bind(this));
        HtmlRouletteTableElement.Emitter.on(HtmlGridEdgeBetsElement.MouseOutBet, this.onMouseOut.bind(this));

        Racetrack.updateNeighboursData();
    }

    //******************************************************************************************************************
    //  MOUSE INTERACTION
    //******************************************************************************************************************

    private onMouseOver(betType: string, selection: string, fromRacetrack: boolean): void {
        this.neighboursPreviewEnd();
        this.handleMouseHover(betType, selection, fromRacetrack, "SELECTED");
    }

    private onMouseOut(betType: string, selection: string, fromRacetrack: boolean): void {
        this.handleMouseHover(betType, selection, fromRacetrack, "NORMAL");
    }

    private handleMouseHover(betType: string, selection: string, fromRacetrack: boolean, caseType: string): void {
        let numbers: Array<number> = [];

        if (fromRacetrack) {
            switch (selection) {
                case "tiers":
                case "orphelins":
                case "voisins":
                case "zeroGame":
                    this.racetrackElements["R_" + selection].goToCase(caseType);
                default:
                    let actionIds = Racetrack.getBetsByRacetrackId(betType + "_" + selection);

                    for (let i: number = 0; i < actionIds.length; i++) {
                        let betTypeArray = actionIds[i].split("_");
                        let currentNumbers = BetManager.getBetNumbersByBetType(
                            betTypeArray[0],
                            parseInt(betTypeArray[1]),
                        );

                        numbers = numbers.concat(currentNumbers);
                    }
            }
        } else {
            numbers = BetManager.getBetNumbersByBetType(betType, parseInt(selection));
        }

        for (let i: number = 0; i < numbers.length; i++) {
            this.racetrackElements["R_" + numbers[i]].goToCase(caseType);
        }
    }

    //******************************************************************************************************************
    //  LAYOUT CHANGE
    //******************************************************************************************************************

    public layoutChanged(): void {
        this.htmlRacetrack.setupInstanceHtmlElement(
            this,
            RouletteRacetrackConfiguration.RacetrackWidth,
            RouletteRacetrackConfiguration.RacetrackHeight,
        );
        StatisticsManager.onLayoutChanged();
    }

    //******************************************************************************************************************
    //  NEIGHBOURS PREVIEW
    //******************************************************************************************************************

    private neighboursPreviewEnd(): void {
        this.neighboursPreviewTimer.stop();

        //reset racetrack elements with highest number of neighbours
        //prevent reset with new (lower) neighbour number
        let lastNeighbourIndex = Racetrack.NeighboursIndex;
        Racetrack.NeighboursIndex = Racetrack.Neighbours.length - 1;

        this.onMouseOut("R", "17", true);
        Racetrack.NeighboursIndex = lastNeighbourIndex;
    }

    private neighboursPreviewStart(): void {
        this.neighboursPreviewEnd();

        this.onMouseOver("R", "17", true);
        this.neighboursPreviewTimer.start(Racetrack.NeighboursPreviewDuration);
    }

    //******************************************************************************************************************
    //  INFO FUNCTIONS
    //******************************************************************************************************************

    public static getBetsByRacetrackId(racetrackId: string): Array<string> {
        switch (racetrackId) {
            case "R_tiers":
                return Racetrack.TiersActions;
            case "R_orphelins":
                return Racetrack.OrphelinsActions;
            case "R_voisins":
                return Racetrack.VoisinsActions;
            case "R_zeroGame":
                return Racetrack.ZeroGameActions;
            default:
                return Racetrack.getNeighboursBets(racetrackId);
        }
    }

    private static getNeighboursBets(racetrackId: string): Array<string> {
        let bets: Array<string> = [];
        let racetrackType = racetrackId.split("_");

        if (racetrackType.length == 2) {
            let middleIndex = parseInt(racetrackType[1]);
            let stripIndex = Racetrack.RacetrackStrip.indexOf(middleIndex);

            bets.push(RouletteActions.ActionBetSingle + middleIndex);

            let index: number;
            for (let i = 0; i < Racetrack.Neighbours[Racetrack.NeighboursIndex]; i++) {
                index =
                    Racetrack.RacetrackStrip[
                        (stripIndex - i - 1 + Racetrack.RacetrackStrip.length) % Racetrack.RacetrackStrip.length
                    ];
                bets.push(RouletteActions.ActionBetSingle + index);
            }

            for (let i = 0; i < Racetrack.Neighbours[Racetrack.NeighboursIndex]; i++) {
                index =
                    Racetrack.RacetrackStrip[
                        (stripIndex + i + 1 + Racetrack.RacetrackStrip.length) % Racetrack.RacetrackStrip.length
                    ];
                bets.push(RouletteActions.ActionBetSingle + index);
            }
        }

        return bets;
    }

    public static neighboursPlus(): boolean {
        if (Racetrack.hasNextNeighbourOption()) {
            Racetrack.NeighboursIndex++;
        }

        Racetrack.updateNeighboursData(true);
        return Racetrack.hasNextNeighbourOption();
    }

    public static hasNextNeighbourOption(): boolean {
        return Racetrack.NeighboursIndex < Racetrack.Neighbours.length - 1;
    }

    public static neighboursMinus(): boolean {
        if (Racetrack.hasPreviousNeighbourOption()) {
            Racetrack.NeighboursIndex--;
        }

        Racetrack.updateNeighboursData(true);
        return Racetrack.hasPreviousNeighbourOption();
    }

    public static hasPreviousNeighbourOption(): boolean {
        return Racetrack.NeighboursIndex > 0;
    }

    private static updateNeighboursData(showPreview: boolean = false): void {
        DataManager.updateDataElement(
            GameData.DataRacetrackNeighbours,
            Racetrack.Neighbours[Racetrack.NeighboursIndex].toString(),
        );
        DataManager.updateGameDataFromModule(
            GameData.DataRacetrackNeighbours,
            Racetrack.Neighbours[Racetrack.NeighboursIndex].toString(),
        );

        if (showPreview && Racetrack.Instance != null) {
            Racetrack.Instance.neighboursPreviewStart();
        }
    }
}

/**
 * Can inherit attributes from BaseElement
 */
export const RacetrackAttributes = {
    /*
     * Name of config for machine.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Racetrack.ts
 **/